<template>
  <div
    class="pb-0 md:pb-4 pt-4 md:pt-0 mb-0 order-1 md:order-0 w-100 row mx-0 px-0 justify-center"
    :class="fullWidth ? 'p-0' : 'pb-0 md:pb-4 pt-4 md:pt-0'"
  >
    <p
      v-if="!formIsProcessing && errorMessage"
      class="bg-danger text-white p-2 rounded-sm col-12 mx-0"
    >
      {{ errorMessage }}
    </p>
    <form
      id="comment-form"
      class="relative p-4 lg:p-5 bg-white shadow rounded-lg mx-0"
      :class="fullWidth ? 'lg:col-10 col-11' : 'mt-5 lg:col-6 md:col-10 col-12'"
    >
      <div
        v-if="!isSignedIn"
        class="text-center text-md"
      >
        <p>{{ t('please_log_in_first') }}</p>
        <div class="col-auto form-group">
          <button
            type="button"
            class="btn btn-outline-primary px-4 font-medium rounded-pill"
            @click="openModal({ id: 'modal-sign-in' })"
          >
            {{ t('log_in') }}
          </button>
        </div>
      </div>
      <!-- Comment -->
      <div v-else>
        <h2 class="h3 mb-4 text-center">
          {{ thankYouMessage === '' ? t('your_comment') : t('thank_you_message') }}
        </h2>
        <span
          v-if="thankYouMessage !== ''"
          class="block text-center"
        >{{ thankYouMessage }}</span>
        <template v-else>
          <div class="stars-rating text-center mb-4">
            <span v-if="ratingPickNumber === 0">{{ t('rate_by_stars') }}</span>
            <RatingStarsSelect
              :rating-pick-number="ratingPickNumber"
              @send-rating="changeRating"
            />
          </div>
          <div
            v-if="ratingPickNumber > 0"
            class="block"
          >
            <span class="mb-1 block text-center">{{ t('now_some_text') }}</span>
            <div class="form-group relative mb-0 text-sm">
              <textarea
                id="comment"
                v-model="comment"
                :placeholder="t('comment')"
                rows="5"
                class="form-control form-control-lg mb-3 text-left rounded-sm"
                required
              />
              <div class="text-center">
                <button
                  :disabled="formIsProcessing"
                  class="btn btn-success  py-3 px-8 xl:px-9 rounded-pill font-medium"
                  type="button"
                  @click="sendReview"
                >
                  {{ t('send') }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </form>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import RatingStarsSelect from '@/components/ratings/RatingStarsSelect.vue'
import { useAppStore } from '@/stores/AppStore'
import { useModalStore } from '@/stores/ModalStore'

const { openModal } = useModalStore()
const { t } = useI18n()

const appStore = useAppStore()

const { isSignedIn } = storeToRefs(appStore)

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
})
const comment = ref()
const formIsProcessing = ref(false)
const errorMessage = ref()
const ratingPickNumber = ref(0)
const thankYouMessage = ref('')

const changeRating = (index) => {
  ratingPickNumber.value = index
}

const sendReview = async () => {
  formIsProcessing.value = true

  try {
    const { data } = await ApiServiceV1.post(`products/${props.product.id}/product_comments`, {
      comment: {
        body: comment.value,
        star_count: ratingPickNumber.value,
      },
    })

    comment.value = ''
    formIsProcessing.value = false
    errorMessage.value = undefined
    ratingPickNumber.value = 0
    thankYouMessage.value = data.msg
    
    openModal({
      id: 'modal-alert',
      title: data.msg,
    })
  } catch (error) {
    formIsProcessing.value = false
    errorMessage.value = error.response.data.msg
    scrollToInfoList()
  }
}

const scrollToInfoList = () => {
  const targetElement = document.querySelector('#comment-form')
  const targetOffsetTop = targetElement.getBoundingClientRect().top + document.documentElement.scrollTop - 300
  window.scrollTo({ top: targetOffsetTop, behavior: 'smooth' })
}
</script>


<i18n>
  {
    "cs": {
    "comment": "Vaše zpráva",
    "log_in": "Přihlásit se",
    "now_some_text": "Nyní můžete napsat hodnocení slovy",
    "please_log_in_first": "Pro přidání komentáře se prosím nejdříve přihlaste.",
    "rate_by_stars": "Nejdříve prosíme ohodnoťte hvězdičkami",
    "send": "Odeslat",
    "thank_you_message": "Děkujeme za zaslání vašeho hodnocení.",
    "your_comment": "Jak jste se zobáním spokojen/a?"
},
    "sk": {
    "comment": "Vaša správa",
    "log_in": "Prihlásiť sa",
    "now_some_text": "Teraz môžete napísať hodnotenie slovami",
    "please_log_in_first": "Pre pridanie komentára sa najskôr, prosím, prihláste.",
    "rate_by_stars": "Najprv, prosím, ohodnoťte hviezdičkami",
    "send": "Odoslať",
    "thank_you_message": "Ďakujeme za zaslanie Vášho hodnotenia.",
    "your_comment": "Ako ste so zobkaním spokojný/á?"
},
    "pl": {
    "comment": "Twoja wiadomość",
    "log_in": "Zaloguj sie",
    "now_some_text": "Teraz możesz napisać recenzję.",
    "please_log_in_first": "Aby dodać komentarz, należy się najpierw zalogować.",
    "rate_by_stars": "Najpierw wystaw gwiazdki",
    "send": "Wyślij",
    "thank_you_message": "Dziękujemy za przesłanie recenzji.",
    "your_comment": "Jak bardzo smakował Ci nasz produkt?"
},
    "hu": {
    "comment": "Megjegyzés",
    "log_in": "Bejelentkezés",
    "now_some_text": "Most már írhatsz véleményt",
    "please_log_in_first": "Hozzászólás hozzáadásához kérjük, először jelentkezz be",
    "rate_by_stars": "Kérjük, értékeld először csillaggal",
    "send": "Küld",
    "thank_you_message": "Köszönjük, hogy elküldted az értékelést!",
    "your_comment": "Mennyire vagy elégedett?"
},
    "de": {
    "comment": "Deine Nachricht",
    "log_in": "Anmelden",
    "now_some_text": "Jetzt kannst du deine schriftliche Bewertung eingeben. ",
    "please_log_in_first": "Bitte melde dich zuerst an, um einen Kommentar hinzuzufügen",
    "rate_by_stars": " Bitte bewerte zuerst mit Sternen. ",
    "send": "Senden",
    "thank_you_message": "Vielen Dank für deine Bewertung",
    "your_comment": "Bist du mit deiner Bestellung zufrieden?"
},
    "at": {
    "comment": "Deine Nachricht",
    "log_in": "Anmelden",
    "now_some_text": "Jetzt kannst du deine schriftliche Bewertung eingeben. ",
    "please_log_in_first": "Bitte melde dich zuerst an, um einen Kommentar hinzuzufügen",
    "rate_by_stars": " Bitte bewerte zuerst mit Sternen. ",
    "send": "Senden",
    "thank_you_message": "Vielen Dank für deine Bewertung",
    "your_comment": "Bist du mit deiner Bestellung zufrieden?"
},
    "ro": {
    "comment": "Mesajul dvs.",
    "log_in": "Autentificare",
    "now_some_text": "Acum puteți scrie o recenzie",
    "please_log_in_first": "Pentru a adăuga un comentariu, vă rugăm să vă autentificați mai întâi.",
    "rate_by_stars": "Vă rugăm să evaluați mai întâi cu stele",
    "send": "Trimite",
    "thank_you_message": "Vă mulțumim că ați trimis recenzia dvs.",
    "your_comment": "Cât de mulțumit/ă sunteți de produs?"
}
  }
</i18n>
